
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Pencil } from "lucide-react";

interface WhiteboardTitleEditorProps {
  whiteboardId: string | null;
  whiteboardName: string;
  onChangeName: (id: string, name: string) => void;
}

const WhiteboardTitleEditor: React.FC<WhiteboardTitleEditorProps> = ({
  whiteboardId,
  whiteboardName,
  onChangeName,
}) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const titleForm = useForm({
    defaultValues: {
      title: whiteboardName
    }
  });

  useEffect(() => {
    titleForm.reset({ title: whiteboardName });
  }, [whiteboardName, titleForm]);

  const handleSaveTitle = (data: { title: string }) => {
    if (whiteboardId && onChangeName) {
      onChangeName(whiteboardId, data.title);
    }
    setIsEditingTitle(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsEditingTitle(false);
    }
  };

  if (isEditingTitle) {
    return (
      <Form {...titleForm}>
        <form 
          onSubmit={titleForm.handleSubmit(handleSaveTitle)} 
          className="flex items-center gap-2"
          onKeyDown={handleKeyDown}
        >
          <FormField
            control={titleForm.control}
            name="title"
            render={({ field }) => (
              <FormItem className="m-0">
                <FormControl>
                  <Input 
                    {...field} 
                    autoFocus 
                    className="text-xl font-semibold h-8" 
                    placeholder="Whiteboard name"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <Button type="submit" size="sm">Save</Button>
          <Button 
            type="button" 
            size="sm" 
            variant="outline" 
            onClick={() => setIsEditingTitle(false)}
          >
            Cancel
          </Button>
        </form>
      </Form>
    );
  }

  return (
    <div 
      className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
      onClick={() => setIsEditingTitle(true)}
    >
      <span>{whiteboardName || "Untitled Whiteboard"}</span>
      <Button 
        variant="ghost" 
        size="icon" 
        className="h-6 w-6" 
        onClick={(e) => {
          e.stopPropagation();
          setIsEditingTitle(true);
        }}
      >
        <Pencil size={12} />
      </Button>
    </div>
  );
};

export default WhiteboardTitleEditor;
